import React, { useState, useEffect } from 'react';
import { Box, Typography, Tooltip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useWebSocket } from '../WebSocketContext';
import { useSnackbar } from '../../Utils/SnackBarContext';
import settingExcelExportIcon from '../../images/setting_excel_export.png';

const SettingExcelExport = ({ caseid }) => {
  const { t } = useTranslation();
  const { ws, subscribeToMessages } = useWebSocket();
  const [isExporting, setIsExporting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { openSnackbar } = useSnackbar();

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      try {
        if (parsedMessage.command === 'exportexcel') {
          setIsExporting(false);
          if (parsedMessage.data.status === 'success') {
            // Trigger download
            const link = document.createElement('a');
            link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${parsedMessage.data.data}`;
            link.download = parsedMessage.data.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            openSnackbar(t('excel_export.success'));
          } else {
            openSnackbar(t('excel_export.error'), 'error');
          }
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
        setIsExporting(false);
      }
    });

    return unsubscribe;
  }, [subscribeToMessages, openSnackbar, t]);

  const handleExport = () => {
    if (isExporting) return;
    setConfirmOpen(true);
  };

  const handleConfirmExport = () => {
    setConfirmOpen(false);
    setIsExporting(true);
    if (ws) {
      ws.send(JSON.stringify({ command: 'exportexcel', caseid }));
    }
  };

  const handleCancelExport = () => {
    setConfirmOpen(false);
  };

  return (
    <Box p={1} width={150}>
      <Box display="flex" flexDirection="column" alignItems="center">
        <Tooltip title={t("Excel Export")}>
          <Box
            sx={{
              position: 'relative',
              display: 'inline-block',
              cursor: isExporting ? 'default' : 'pointer',
              opacity: isExporting ? 0.6 : 1,
            }}
            onClick={handleExport}
          >
            <img
              src={settingExcelExportIcon}
              alt="Excel Export"
              style={{ width: 48, height: 48 }}
            />
            {isExporting && (
              <CircularProgress
                size={48}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
              />
            )}
          </Box>
        </Tooltip>
        <Typography variant="caption">Excel</Typography>
      </Box>

      <Dialog open={confirmOpen} onClose={handleCancelExport}>
        <DialogTitle>{t('excel_export.confirm_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('excel_export.confirm_message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelExport} color="secondary">
            {t('cancel')}
          </Button>
          <Button onClick={handleConfirmExport} color="primary" variant="contained">
            {t('export')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingExcelExport;
